import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Activity, ExternalLink, ZapOff } from 'lucide-react';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import { DataTable, type Column } from '../components/data-table';

interface IssueRow {
  readonly id: string;
  readonly project: string;
  readonly title: string;
  readonly culprit: string | null;
  readonly level: string;
  readonly count: number;
  readonly userCount: number;
  readonly firstSeen: string;
  readonly lastSeen: string;
  readonly permalink: string;
}

interface ErrorsResponse {
  readonly configured: boolean;
  readonly projects: readonly string[];
  readonly issues: readonly IssueRow[];
  readonly crashFreeByProject: ReadonlyArray<{
    readonly project: string;
    readonly crashFreePct: number | null;
  }>;
}

const fmtInt = new Intl.NumberFormat('en-US');

function levelChip(level: string) {
  if (level === 'fatal' || level === 'error')
    return <span className="nw-chip nw-chip--error">{level}</span>;
  if (level === 'warning')
    return <span className="nw-chip nw-chip--warning">{level}</span>;
  return <span className="nw-chip nw-chip--info">{level}</span>;
}

const cols: readonly Column<IssueRow>[] = [
  {
    key: 'project',
    header: 'Project',
    width: '160px',
    render: (r) => <span className="nw-chip">{r.project}</span>,
  },
  {
    key: 'level',
    header: 'Level',
    width: '90px',
    render: (r) => levelChip(r.level),
  },
  {
    key: 'title',
    header: 'Title',
    render: (r) => (
      <div>
        <div style={{ fontWeight: 500 }}>{r.title}</div>
        {r.culprit && (
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
            <code style={{ fontFamily: 'SF Mono, Menlo, Consolas, monospace' }}>
              {r.culprit}
            </code>
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'count',
    header: 'Events',
    width: '90px',
    align: 'right',
    render: (r) => fmtInt.format(r.count),
  },
  {
    key: 'userCount',
    header: 'Users',
    width: '80px',
    align: 'right',
    render: (r) => fmtInt.format(r.userCount),
  },
  {
    key: 'lastSeen',
    header: 'Last seen',
    width: '160px',
    render: (r) => (
      <span title={r.lastSeen} style={{ color: 'var(--text-secondary)' }}>
        {formatDistanceToNow(new Date(r.lastSeen), { addSuffix: true })}
      </span>
    ),
  },
  {
    key: 'link',
    header: '',
    width: '50px',
    align: 'center',
    render: (r) => (
      <a
        href={r.permalink}
        target="_blank"
        rel="noopener noreferrer"
        title="Open in Sentry"
        style={{ color: 'var(--accent)' }}
      >
        <ExternalLink size={16} strokeWidth={2} />
      </a>
    ),
  },
];

export function Errors() {
  const query = useQuery({
    queryKey: queryKeys.errors(),
    queryFn: async () => {
      const res = await apiClient.get<ErrorsResponse>('/owner/errors');
      return res.data;
    },
  });

  const data = query.data;

  return (
    <>
      <header className="nw-page-header">
        <h1 className="nw-page-header__title">Errors</h1>
        <p className="nw-page-header__subtitle">
          Top unresolved Sentry issues in the last 24 h, with crash-free %
          per mobile project.
        </p>
      </header>

      {!query.isLoading && data && !data.configured && (
        <div className="nw-placeholder">
          <h2 className="nw-placeholder__title">Sentry not configured</h2>
          <p>
            Set SENTRY_AUTH_TOKEN, SENTRY_ORG_SLUG, and SENTRY_PROJECT_SLUGS
            on admin-api to populate this page.
          </p>
        </div>
      )}

      {data?.configured && (
        <div className="nw-kpi-grid">
          {data.crashFreeByProject.map((c) => (
            <div className="nw-kpi" key={c.project}>
              <div className="nw-kpi__head">
                <span className="nw-kpi__icon">
                  <ZapOff size={18} strokeWidth={2} />
                </span>
                <span className="nw-kpi__label">{c.project} crash-free</span>
              </div>
              <div
                className={
                  c.crashFreePct === null
                    ? 'nw-kpi__value nw-kpi__value--null'
                    : 'nw-kpi__value'
                }
              >
                {c.crashFreePct === null
                  ? '—'
                  : `${c.crashFreePct.toFixed(2)}%`}
              </div>
            </div>
          ))}
          <div className="nw-kpi">
            <div className="nw-kpi__head">
              <span className="nw-kpi__icon">
                <Activity size={18} strokeWidth={2} />
              </span>
              <span className="nw-kpi__label">Unresolved issues</span>
            </div>
            <div className="nw-kpi__value">{fmtInt.format(data.issues.length)}</div>
            <span className="nw-kpi__delta-note">across {data.projects.length} projects</span>
          </div>
        </div>
      )}

      {data?.configured && (
        <DataTable
          columns={cols}
          rows={data.issues}
          rowKey={(r) => r.id}
          loading={query.isLoading}
          error={query.error ? (query.error as Error).message : null}
          emptyMessage="No unresolved issues — nice."
        />
      )}
    </>
  );
}
